import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  DndContext, DragOverlay, MouseSensor, TouchSensor, pointerWithin, useSensor, useSensors,
  type DragEndEvent, type DragMoveEvent, type DragStartEvent,
} from '@dnd-kit/core';
import SiteHeader from '../components/SiteHeader';
import CardPreview from '../components/CardPreview';
import { NumberPrompt } from '../components/Modals';
import { decksApi } from '../api/decksApi';
import { useAuth } from '../auth/AuthContext';
import cardBack from '../assets/card-back.jpg';
import type { Deck } from '../../../shared/types';
import {
  buildBoard, shuffle, zoneCard, removeFrom, putZone, putPile, isFieldZone, isCounterZone,
  slicesFor, sliceAt, snapCenterToCursor, anchorStyle,
  type Board, type Pos, type SpinState,
} from '../board/model';
import {
  PlayCard, Zone, PosOption, SliceOverlay, ShuffleFx, OverlayImg, useCountUp, useHalo, ViewerStrip, CounterSource, HandZone, DotMenu, DeckPeekViewer, RevealSelectViewer, StatEditor,
} from '../board/components';
import type { Inst } from '../board/model';

export default function PlaytestPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { loading: authLoading, getToken, refresh } = useAuth();

  const [deckData, setDeckData] = useState<Deck | null>(null);
  const [board, setBoard] = useState<Board | null>(null);
  const [loadError, setLoadError] = useState('');
  const [viewed, setViewed] = useState<string | null>(null);

  const [active, setActive] = useState<{ kind: 'card' | 'counter'; name?: string } | null>(null);
  const [dropHint, setDropHint] = useState<{ zone: string; slice: number } | null>(null);
  const [prevPos, setPrevPos] = useState<{ zone: string; slice: number } | null>(null); // where the dragged card sat
  const [detailCard, setDetailCard] = useState<string | null>(null);   // mobile detail modal
  const [menuFor, setMenuFor] = useState<string | null>(null);         // zone whose card's position menu is open
  const [viewer, setViewer] = useState<'deck' | 'gy' | 'extra' | 'banished' | null>(null);
  const [lpYou, setLpYou] = useState(4000);
  const [lpOpp, setLpOpp] = useState(4000);
  const [lpAmt, setLpAmt] = useState('');
  const [dice, setDice] = useState<number | null>(null);
  const [diceState, setDiceState] = useState<SpinState>('idle');
  const [coin, setCoin] = useState<'Heads' | 'Tails' | null>(null);
  const [coinState, setCoinState] = useState<SpinState>('idle');
  const [confirmReset, setConfirmReset] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [shuffleTick, setShuffleTick] = useState(0);   // >0 while the shuffle animation plays; also the fx key
  const [deckPeek, setDeckPeek] = useState<{ cards: Inst[]; reveal: boolean } | null>(null);
  const [revealSel, setRevealSel] = useState<{ n: number; picked: string[] } | null>(null);
  const [numPrompt, setNumPrompt] = useState<{ title: string; max: number; onSubmit: (n: number) => void } | null>(null);
  const [statEdit, setStatEdit] = useState<{ zone: string; name: string; atk?: number; def?: number } | null>(null);
  const lastDrawn = useRef<string | null>(null);
  const spinTimers = useRef<number[]>([]);
  const shuffleTimer = useRef<number | undefined>(undefined);
  useEffect(() => () => {
    spinTimers.current.forEach(id => window.clearInterval(id));
    if (shuffleTimer.current) window.clearTimeout(shuffleTimer.current);
  }, []);
  const startPointer = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  // Touch uses a short hold-delay so a quick swipe still scrolls the page and a
  // hold picks the card up; releasing a hold in place opens the position menu
  // (handled in onDragEnd). Mouse uses distance; desktop's hold-menu comes from
  // the press-and-hold timer in PlayCard.
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } }),
  );

  useEffect(() => {
    if (authLoading) return;
    let cancelled = false;
    setLoadError('');
    decksApi.get(id, getToken, refresh)
      .then(d => { if (!cancelled) { setDeckData(d); setBoard(buildBoard(d)); } })
      .catch(e => { if (!cancelled) setLoadError(e instanceof Error ? e.message : 'Deck not found'); });
    return () => { cancelled = true; };
  }, [id, authLoading, getToken, refresh]);

  // ── actions ───────────────────────────────────────────────────────────────
  const draw = () => { if (deckPeek) return; setBoard(b => { if (!b || !b.deck.length) return b; const c = b.deck[0]; lastDrawn.current = c.uid; return { ...b, deck: b.deck.slice(1), hand: [...b.hand, c] }; }); };
  // Excavate: mill the top card of the deck straight to the graveyard.
  const excavate = () => setBoard(b => (b && b.deck.length ? { ...b, deck: b.deck.slice(1), gy: [...b.gy, b.deck[0]] } : b));
  // Hand ⋯ actions. Reveal is opponent-facing (a no-op here in solo playtest).
  const discardRandom = () => setBoard(b => { if (!b || !b.hand.length) return b; const i = Math.floor(Math.random() * b.hand.length); return { ...b, hand: b.hand.filter((_, j) => j !== i), gy: [...b.gy, b.hand[i]] }; });
  // Solo: no opponent to reveal to, but still flash the halo on the card(s).
  const reveal = (uids: string[]) => { uids.forEach(u => flash(u)); };
  // Deck ⋯ actions: peek at (or reveal) the top N; reorder; return in order.
  // A custom "how many?" popup (never the browser prompt).
  const promptN = (title: string, max: number, onSubmit: (n: number) => void) => { if (max > 0) setNumPrompt({ title, max, onSubmit }); };
  const openPeek = (revealMode: boolean) => { if (!board || !board.deck.length) return; promptN('How many cards from the top?', board.deck.length, (n) => setDeckPeek({ cards: board.deck.slice(0, n).map(c => ({ uid: c.uid, name: c.name })), reveal: revealMode })); };
  // "Reveal Any N": browse the deck + pick up to N (opponent-facing → inert in solo playtest).
  const openRevealAny = () => { if (!board || !board.deck.length) return; promptN('How many cards to reveal?', board.deck.length, (n) => setRevealSel({ n, picked: [] })); };
  const toggleRevealPick = (uid: string) => setRevealSel(s => { if (!s) return s; const has = s.picked.includes(uid); const picked = has ? s.picked.filter(u => u !== uid) : (s.picked.length < s.n ? [...s.picked, uid] : s.picked); return { ...s, picked }; });
  const peekReorder = (from: number, to: number) => setDeckPeek(dp => { if (!dp) return dp; const cards = [...dp.cards]; const [m] = cards.splice(from, 1); cards.splice(to, 0, m); return { ...dp, cards }; });
  const peekClose = () => { setDeckPeek(dp => { if (dp) setBoard(b => (b ? { ...b, deck: [...dp.cards, ...b.deck.slice(dp.cards.length)] } : b)); return null; }); };
  const handMenu = [
    { label: 'Discard random card', onClick: discardRandom },
    { label: 'Reveal random card', onClick: () => { const b = board; if (b && b.hand.length) reveal([b.hand[Math.floor(Math.random() * b.hand.length)].uid]); } },
    { label: 'Reveal last drawn', onClick: () => { if (lastDrawn.current) reveal([lastDrawn.current]); } },
    { label: 'Reveal hand', onClick: () => { if (board) reveal(board.hand.map(c => c.uid)); } },
  ];
  const deckMenu = [
    { label: 'Reveal Any N Cards', onClick: openRevealAny },
    { label: 'Reveal Top N Cards', onClick: () => openPeek(true) },
    { label: 'Look at Top N Cards', onClick: () => openPeek(false) },
  ];
  const shuffleDeck = () => {
    setBoard(b => (b ? { ...b, deck: shuffle(b.deck) } : b));
    if (board && board.deck.length > 1) {
      setShuffleTick(t => t + 1);                        // new key → riffle animation replays
      if (shuffleTimer.current) window.clearTimeout(shuffleTimer.current);
      shuffleTimer.current = window.setTimeout(() => setShuffleTick(0), 520);
    }
  };
  // Leaving the deck view shuffles it (so you can't exploit the peeked order);
  // switching between GY / Extra / Banished views never shuffles.
  const changeViewer = (next: 'deck' | 'gy' | 'extra' | 'banished' | null) => {
    if (viewer === 'deck' && next !== 'deck') shuffleDeck();
    setViewer(next);
  };
  // Right-click a pile zone (or its 🔍) = clicking its 🔍.
  const viewCtx = (pile: 'deck' | 'gy' | 'extra' | 'banished') => (e: { preventDefault: () => void }) => { e.preventDefault(); changeViewer(viewer === pile ? null : pile); };
  const reset = () => { if (deckData) { setBoard(buildBoard(deckData)); setViewer(null); setMenuFor(null); setLpYou(4000); setLpOpp(4000); } };
  const setPos = (loc: string, faceUp: boolean, pos: Pos) =>
    setBoard(b => { if (!b) return b; const c = zoneCard(b, loc); return c ? putZone(b, loc, { ...c, faceUp, pos }) : b; });
  const applyLp = (who: 'you' | 'opp', sign: 1 | -1) => {
    const n = Math.abs(parseInt(lpAmt, 10) || 0);
    if (!n) return;
    (who === 'you' ? setLpYou : setLpOpp)(v => Math.max(0, v + sign * n));
  };
  const placeCard = (uid: string, src: string, dest: string, faceUp: boolean, pos: Pos) =>
    setBoard(b => { if (!b) return b; const sc = isFieldZone(src) ? zoneCard(b, src) : null; const [b2, inst] = removeFrom(b, src, uid); return inst ? putZone(b2, dest, { ...inst, faceUp, pos, counters: sc?.counters, atk: sc?.atk, def: sc?.def }) : b; });
  const setStats = (zone: string, atk: number, def: number) => setBoard(b => { if (!b) return b; const c = zoneCard(b, zone); return c ? putZone(b, zone, { ...c, atk, def }) : b; });
  const pileMove = (uid: string, src: string, dest: string) =>
    setBoard(b => { if (!b) return b; const [b2, inst] = removeFrom(b, src, uid); return inst ? putPile(b2, dest, inst) : b; });
  // Into the deck at the top (index 0, drawn next) or the bottom.
  const deckMove = (uid: string, src: string, toTop: boolean) =>
    setBoard(b => {
      if (!b) return b;
      const [b2, inst] = removeFrom(b, src, uid);
      return inst ? { ...b2, deck: toTop ? [inst, ...b2.deck] : [...b2.deck, inst] } : b;
    });
  // Drop onto the deck's "Shuffle" slice: the card joins the deck and it reshuffles.
  const shuffleInto = (uid: string, src: string) => {
    setBoard(b => { if (!b) return b; const [b2, inst] = removeFrom(b, src, uid); return inst ? { ...b2, deck: shuffle([...b2.deck, inst]) } : b; });
    setShuffleTick(t => t + 1);
    if (shuffleTimer.current) window.clearTimeout(shuffleTimer.current);
    shuffleTimer.current = window.setTimeout(() => setShuffleTick(0), 520);
  };
  // ── counters ──
  const addCounter = (zone: string) =>
    setBoard(b => { if (!b) return b; const c = zoneCard(b, zone); return c ? putZone(b, zone, { ...c, counters: (c.counters ?? 0) + 1 }) : b; });
  const removeCounter = (zone: string) =>
    setBoard(b => { if (!b) return b; const c = zoneCard(b, zone); return c ? putZone(b, zone, { ...c, counters: Math.max(0, (c.counters ?? 0) - 1) }) : b; });
  const moveCounter = (src: string, dest: string) =>
    setBoard(b => {
      if (!b) return b;
      const sc = zoneCard(b, src);
      if (!sc || (sc.counters ?? 0) <= 0) return b;
      let nb = putZone(b, src, { ...sc, counters: (sc.counters ?? 0) - 1 });
      const dc = zoneCard(nb, dest);
      if (dc) nb = putZone(nb, dest, { ...dc, counters: (dc.counters ?? 0) + 1 });
      return nb;
    });
  // Build-up: ~18 ticks × 50ms, each different from the last (and the very last
  // tick different from the final too, so the settle is visible), then a fair,
  // independent final result. Locked while rolling and for 250ms after.
  const spin = <T,>(
    state: SpinState, setState: (s: SpinState) => void, setVal: (v: T) => void, current: T | null,
    tickPick: (prev: T | null, isLast: boolean, finalVal: T) => T, finalPick: () => T,
  ) => {
    if (state !== 'idle') return;
    setState('rolling');
    const finalVal = finalPick();
    const TICKS = 18;
    let prev = current;
    let i = 0;
    const iv = window.setInterval(() => {
      if (i >= TICKS) {
        window.clearInterval(iv);
        setVal(finalVal);
        setState('cooldown');
        spinTimers.current.push(window.setTimeout(() => setState('idle'), 250));
        return;
      }
      const v = tickPick(prev, i === TICKS - 1, finalVal);
      prev = v;
      setVal(v);
      i++;
    }, 50);
    spinTimers.current.push(iv);
  };
  const rollDice = () => spin(diceState, setDiceState, setDice, dice,
    (prev, isLast, finalVal) => {
      const avoid = new Set<number>();
      if (prev != null) avoid.add(prev);
      if (isLast) avoid.add(finalVal);                       // last tick ≠ final → visible settle
      let v = 1 + Math.floor(Math.random() * 6);
      while (avoid.has(v)) v = 1 + Math.floor(Math.random() * 6);
      return v;
    },
    () => 1 + Math.floor(Math.random() * 6));                 // fair, independent final
  const flipCoin = () => spin(coinState, setCoinState, setCoin, coin,
    (prev, isLast, finalVal) =>
      isLast ? (finalVal === 'Heads' ? 'Tails' : 'Heads')    // last tick opposite the final
             : (prev === 'Heads' ? 'Tails' : 'Heads'),       // otherwise just alternate
    () => (Math.random() < 0.5 ? 'Heads' : 'Tails'));

  const pointerFrom = (delta: { x: number; y: number }) => ({ x: startPointer.current.x + delta.x, y: startPointer.current.y + delta.y });

  function onDragStart(e: DragStartEvent) {
    const ev = e.activatorEvent as MouseEvent & TouchEvent;
    if (ev.touches && ev.touches[0]) startPointer.current = { x: ev.touches[0].clientX, y: ev.touches[0].clientY };
    else startPointer.current = { x: ev.clientX ?? 0, y: ev.clientY ?? 0 };
    const kind = e.active.data.current?.kind as string | undefined;
    if (kind === 'counter-add' || kind === 'counter-move') { setActive({ kind: 'counter' }); setPrevPos(null); }
    else {
      setActive({ kind: 'card', name: e.active.data.current?.name as string });
      // Remember where a placed card currently sits, to flag it in the chooser.
      const src = e.active.data.current?.src as string;
      const c = board && isFieldZone(src) ? zoneCard(board, src) : null;
      setPrevPos(c ? { zone: src, slice: slicesFor(src).findIndex(s => s.faceUp === c.faceUp && s.pos === c.pos) } : null);
    }
    setDetailCard(null);
    setMenuFor(null);
    document.body.classList.add('dnd-dragging');
  }
  // Show the position-quadrant menu on an empty field zone while hovering it.
  function onDragMove(e: DragMoveEvent) {
    const kind = e.active.data.current?.kind as string | undefined;
    if (kind === 'counter-add' || kind === 'counter-move') { setDropHint(h => (h ? null : h)); return; }
    const src = e.active.data.current?.src as string;
    const overId = String(e.over?.id ?? '');
    // Show the chooser over the deck (Top/Bottom), any empty field zone, OR the
    // card's OWN zone (so a placed card can be dragged to a new position).
    if (board && (overId === 'deck' || (isFieldZone(overId) && (!zoneCard(board, overId) || overId === src)))) {
      const p = pointerFrom(e.delta);
      const q = sliceAt(overId, p.x, p.y);
      setDropHint(h => (h && h.zone === overId && h.slice === q ? h : { zone: overId, slice: q }));
    } else {
      setDropHint(h => (h ? null : h));
    }
  }
  function onDragEnd(e: DragEndEvent) {
    document.body.classList.remove('dnd-dragging');
    setActive(null);
    setDropHint(null);
    setPrevPos(null);
    if (!board) return;
    const kind = e.active.data.current?.kind as string | undefined;
    const src = e.active.data.current?.src as string;
    const dest = String(e.over?.id ?? '');
    // Counter drags: add drops a counter on the hovered card; move relocates one
    // to another allowed card, or removes it when released anywhere else.
    if (kind === 'counter-add') {
      if (isCounterZone(dest) && zoneCard(board, dest)) addCounter(dest);
      return;
    }
    if (kind === 'counter-move') {
      if (dest === src) return;                                          // back on itself → keep
      if (isCounterZone(dest) && zoneCard(board, dest)) moveCounter(src, dest);
      else removeCounter(src);                                           // off a card → gone
      return;
    }
    const uid = e.active.data.current?.uid as string;
    // Picked up and released without really moving → a press-and-hold: open the
    // position menu for a placed card instead of moving anything.
    if (Math.hypot(e.delta.x, e.delta.y) < 10) {
      if (isFieldZone(src)) setMenuFor(src);
      return;
    }
    if (!dest) return;
    if (dest === 'deck') {
      const p = pointerFrom(e.delta);
      const q = sliceAt('deck', p.x, p.y);
      if (q === 2) shuffleInto(uid, src);                     // right slice → shuffle into deck
      else deckMove(uid, src, q === 0);                       // top half → top of deck
      return;                                                 // stays in the deck — no auto-shuffle/close
    }
    if (isFieldZone(dest)) {
      // Occupied by a *different* card → reject. Dropping back on its own zone
      // re-positions the card in place.
      if (zoneCard(board, dest) && dest !== src) return;
      const p = pointerFrom(e.delta);
      const q = slicesFor(dest)[sliceAt(dest, p.x, p.y)];     // slice → position
      if (dest === src) setPos(dest, q.faceUp, q.pos);
      else placeCard(uid, src, dest, q.faceUp, q.pos);
    } else if (dest !== src) {
      pileMove(uid, src, dest);                               // gy / extra / hand / banished
    } else {
      return;                                                 // dropped back on its own pile
    }
    // A card pulled from the open deck view and placed elsewhere shuffles the
    // remaining deck and closes the view.
    if (src === 'deck') changeViewer(null);
  }
  function onDragCancel() { document.body.classList.remove('dnd-dragging'); setActive(null); setDropHint(null); setPrevPos(null); }

  const viewerCards = useMemo(() => {
    if (!board || !viewer) return [];
    return viewer === 'deck' ? board.deck : viewer === 'gy' ? board.gy : viewer === 'banished' ? board.banished : board.extra;
  }, [board, viewer]);
  const viewerTitle = viewer === 'deck' ? 'Deck' : viewer === 'gy' ? 'Graveyard' : viewer === 'banished' ? 'Banished' : 'Extra Deck';

  // Life points tick toward their new value instead of jumping.
  const shownYou = useCountUp(lpYou);
  const shownOpp = useCountUp(lpOpp);
  const { haloed, flash } = useHalo();

  if (loadError) return <div><SiteHeader /><main className="page page--wide"><h1>Playtest</h1><p className="error">{loadError}</p><p><Link to="/decks">← Decks</Link></p></main></div>;
  if (!board) return <div><SiteHeader /><main className="page page--wide"><p className="muted">Loading…</p></main></div>;

  const menuCard = menuFor ? zoneCard(board, menuFor) : null;

  return (
    <div>
      <SiteHeader />
      <DndContext sensors={sensors} collisionDetection={pointerWithin} autoScroll={false} onDragStart={onDragStart} onDragMove={onDragMove} onDragEnd={onDragEnd} onDragCancel={onDragCancel}>
        <main className="page playtest-layout">
          {/* Left: big persistent card preview */}
          <aside className="pt-left">
            <div className="pt-preview">
              <CardPreview name={viewed} />
            </div>
          </aside>

          <div className="pt-board">
            {/* Skill zone + the pile viewer + Banished share this top row.
                Banished sits at the far right, directly above the GY column. */}
            <div className="pt-skillrow">
              <Zone id="skill" className="pt-skill-zone pt-zone-field" label="Skill">
                {board.skill && <PlayCard inst={board.skill} src="skill" faceUp={board.skill.faceUp} pos={board.skill.pos} halo={haloed.has(board.skill.uid)} onView={setViewed} onOpen={setDetailCard} onHold={() => setMenuFor('skill')} onHalo={() => flash(board.skill!.uid)} />}
              </Zone>
              <div className="pt-viewer-inline">
                {statEdit ? (
                  <StatEditor name={statEdit.name} atk={statEdit.atk} def={statEdit.def}
                    onConfirm={(a, d) => { setStats(statEdit.zone, a, d); setStatEdit(null); }} onCancel={() => setStatEdit(null)} />
                ) : deckPeek ? (
                  <DeckPeekViewer cards={deckPeek.cards} reveal={deckPeek.reveal} onReorder={peekReorder} onClose={peekClose} onView={setViewed} />
                ) : revealSel && board ? (
                  <RevealSelectViewer cards={board.deck} n={revealSel.n} selected={revealSel.picked}
                    onToggle={toggleRevealPick} onConfirm={() => { setRevealSel(null); shuffleDeck(); }} onCancel={() => { setRevealSel(null); shuffleDeck(); }} onView={setViewed} />
                ) : viewer ? (
                  <>
                    <div className="pt-viewer-head">
                      <h3>{viewerTitle} ({viewerCards.length})</h3>
                      <button type="button" className="preview-close" onClick={() => changeViewer(null)} title="Close">✕</button>
                    </div>
                    {viewerCards.length === 0
                      ? <p className="muted">Empty.</p>
                      : <ViewerStrip cards={viewerCards} src={viewer} onView={setViewed} onToHand={uid => pileMove(uid, viewer, 'hand')} />}
                  </>
                ) : (
                  <p className="muted pt-viewer-hint">Use 🔍 on Deck, GY, Banished, or Extra to browse those cards here.</p>
                )}
              </div>
              <Zone id="banished" className="pt-zone-pile pt-zone-banished" label={`Banished (${board.banished.length})`} onContext={viewCtx('banished')}>
                {board.banished.length > 0 && <PlayCard inst={board.banished[board.banished.length - 1]} src="banished" halo={haloed.has(board.banished[board.banished.length - 1].uid)} onView={setViewed} onOpen={setDetailCard} />}
                <button type="button" className="pt-eye" onClick={e => { e.stopPropagation(); changeViewer(viewer === 'banished' ? null : 'banished'); }} title="View Banished">🔍</button>
              </Zone>
            </div>

            {/* Field: 2 rows × 5 cols.
                Row 1: Field | Monster | Monster | Monster | GY
                Row 2: Extra | Spell/Trap | Spell/Trap | Spell/Trap | Deck */}
            <div className="pt-field">
              <Zone id="fieldspell" className="pt-zone-field" label="Field">
                {board.fieldSpell && <PlayCard inst={board.fieldSpell} src="fieldspell" faceUp={board.fieldSpell.faceUp} pos={board.fieldSpell.pos} counters={board.fieldSpell.counters ?? 0} halo={haloed.has(board.fieldSpell.uid)} onView={setViewed} onOpen={setDetailCard} onHold={() => setMenuFor('fieldspell')} onHalo={() => flash(board.fieldSpell!.uid)} onCounterInc={() => addCounter('fieldspell')} onCounterDec={() => removeCounter('fieldspell')} />}
              </Zone>
              {[0, 1, 2].map(i => (
                <Zone key={`mon-${i}`} id={`mon-${i}`} className="pt-zone-mon" label="Monster">
                  {board.monsters[i] && <PlayCard inst={board.monsters[i]!} src={`mon-${i}`} faceUp={board.monsters[i]!.faceUp} pos={board.monsters[i]!.pos} counters={board.monsters[i]!.counters ?? 0} atk={board.monsters[i]!.atk} def={board.monsters[i]!.def} stats halo={haloed.has(board.monsters[i]!.uid)} onView={setViewed} onOpen={setDetailCard} onHold={() => setMenuFor(`mon-${i}`)} onHalo={() => flash(board.monsters[i]!.uid)} onCounterInc={() => addCounter(`mon-${i}`)} onCounterDec={() => removeCounter(`mon-${i}`)} onStatClick={() => setStatEdit({ zone: `mon-${i}`, name: board.monsters[i]!.name, atk: board.monsters[i]!.atk, def: board.monsters[i]!.def })} />}
                </Zone>
              ))}
              <Zone id="gy" className="pt-zone-pile" label={`GY (${board.gy.length})`} onContext={viewCtx('gy')}>
                {board.gy.length > 0 && <PlayCard inst={board.gy[board.gy.length - 1]} src="gy" halo={haloed.has(board.gy[board.gy.length - 1].uid)} onView={setViewed} onOpen={setDetailCard} />}
                <button type="button" className="pt-eye" onClick={e => { e.stopPropagation(); changeViewer(viewer === 'gy' ? null : 'gy'); }} title="View Graveyard">🔍</button>
              </Zone>

              <Zone id="extra" className="pt-zone-pile" label={`Extra (${board.extra.length})`} onContext={viewCtx('extra')}>
                {board.extra.length > 0 && <div className="play-card"><img src={cardBack} className="play-cardback" alt="" /></div>}
                <button type="button" className="pt-eye" onClick={e => { e.stopPropagation(); changeViewer(viewer === 'extra' ? null : 'extra'); }} title="View Extra Deck">🔍</button>
              </Zone>
              {[0, 1, 2].map(i => (
                <Zone key={`st-${i}`} id={`st-${i}`} className="pt-zone-st" label="Spell/Trap">
                  {board.spells[i] && <PlayCard inst={board.spells[i]!} src={`st-${i}`} faceUp={board.spells[i]!.faceUp} pos={board.spells[i]!.pos} counters={board.spells[i]!.counters ?? 0} halo={haloed.has(board.spells[i]!.uid)} onView={setViewed} onOpen={setDetailCard} onHold={() => setMenuFor(`st-${i}`)} onHalo={() => flash(board.spells[i]!.uid)} onCounterInc={() => addCounter(`st-${i}`)} onCounterDec={() => removeCounter(`st-${i}`)} />}
                </Zone>
              ))}
              <Zone id="deck" className="pt-zone-pile" label={`Deck (${board.deck.length})`} onClick={draw} onContext={viewCtx('deck')}>
                {board.deck.length > 0 && <div className="play-card"><img src={cardBack} className="play-cardback" alt="" /></div>}
                {shuffleTick > 0 && <ShuffleFx key={shuffleTick} />}
                <DotMenu items={deckMenu} up />
                <button type="button" className="pt-eye" onClick={e => { e.stopPropagation(); changeViewer(viewer === 'deck' ? null : 'deck'); }} title="View / search Deck">🔍</button>
              </Zone>
            </div>

            {/* Hand */}
            <HandZone hand={board.hand} menu={<DotMenu items={handMenu} up />} halo={uid => haloed.has(uid)} onView={setViewed} onOpen={setDetailCard} />
          </div>

          {/* Right: life points + game controls */}
          <aside className="pt-controls">
            <div className="pt-lp-panel">
              <div className="pt-lp-row"><span>You</span><strong className="pt-lp-val">{shownYou}</strong>
                <button type="button" onClick={() => applyLp('you', 1)} aria-label="add to your LP">+</button>
                <button type="button" onClick={() => applyLp('you', -1)} aria-label="subtract from your LP">−</button>
              </div>
              <div className="pt-lp-row"><span>Opp</span><strong className="pt-lp-val">{shownOpp}</strong>
                <button type="button" onClick={() => applyLp('opp', 1)} aria-label="add to opponent LP">+</button>
                <button type="button" onClick={() => applyLp('opp', -1)} aria-label="subtract from opponent LP">−</button>
              </div>
              <input type="number" inputMode="numeric" className="pt-lp-input" placeholder="Amount"
                value={lpAmt} onChange={e => setLpAmt(e.target.value)} />
            </div>
            <div className="pt-tools">
              <button type="button" className="pt-tool" onClick={rollDice} disabled={diceState !== 'idle'}>
                <span className="pt-tool-label">🎲 Dice</span>
                <span className={`pt-tool-result${dice == null ? ' placeholder' : ''}${diceState === 'rolling' ? ' rolling' : ''}`}>{dice ?? 'Roll'}</span>
              </button>
              <button type="button" className="pt-tool" onClick={flipCoin} disabled={coinState !== 'idle'}>
                <span className="pt-tool-label">🪙 Coin</span>
                <span className={`pt-tool-result${coin == null ? ' placeholder' : ''}${coinState === 'rolling' ? ' rolling' : ''}`}>{coin ?? 'Flip'}</span>
              </button>
            </div>
            <div className="pt-game-btns">
              <button type="button" onClick={draw} disabled={!board.deck.length || !!deckPeek}>Draw ({board.deck.length})</button>
              <button type="button" onClick={excavate} disabled={!board.deck.length || !!deckPeek}>Excavate</button>
              <button type="button" onClick={() => (viewer === 'deck' ? changeViewer(null) : shuffleDeck())}>Shuffle</button>
            </div>
            <CounterSource />
            <button type="button" onClick={() => setConfirmReset(true)} className="btn-danger pt-reset-btn">Reset</button>
            <button type="button" onClick={() => setConfirmLeave(true)} className="pt-back-btn">← Back to deck</button>
          </aside>
        </main>

        <DragOverlay dropAnimation={null} modifiers={[snapCenterToCursor]}>
          {active?.kind === 'counter' ? (
            <div className="play-counter play-counter-drag">●</div>
          ) : active?.kind === 'card' ? (
            (() => {
              // Deck drops don't have an orientation — always show the card face.
              const isDeck = dropHint?.zone === 'deck';
              const hp = dropHint && !isDeck ? slicesFor(dropHint.zone)[dropHint.slice] : null;
              return (
                <div className={`play-card drag-overlay${hp?.pos === 'def' ? ' play-def' : ''}`}>
                  {hp && !hp.faceUp
                    ? <img src={cardBack} className="play-cardback" alt="" />
                    : <OverlayImg name={active.name!} />}
                </div>
              );
            })()
          ) : null}
        </DragOverlay>

        {/* Position-slice chooser drawn over the hovered empty zone during a drag */}
        {dropHint && <SliceOverlay hint={dropHint} prevSlice={prevPos && prevPos.zone === dropHint.zone ? prevPos.slice : -1} />}
      </DndContext>

      {/* "How many?" custom popup (deck ⋯ actions) */}
      {numPrompt && (
        <NumberPrompt title={numPrompt.title} max={numPrompt.max}
          onSubmit={n => { const cb = numPrompt.onSubmit; setNumPrompt(null); cb(n); }} onCancel={() => setNumPrompt(null)} />
      )}

      {/* Deck peek / reveal-any / stat editor render in the central .pt-viewer-inline panel. */}

      {/* Card detail popup (tap a card) */}
      {detailCard && (
        <div className="pt-detail-backdrop" onClick={() => setDetailCard(null)}>
          <div className="pt-detail" onClick={e => e.stopPropagation()}>
            <div className="pt-detail-bar">
              <span className="pt-detail-title">Card details</span>
              <button type="button" className="preview-close" onClick={() => setDetailCard(null)} title="Close">✕</button>
            </div>
            <div className="pt-detail-body"><CardPreview name={detailCard} /></div>
          </div>
        </div>
      )}

      {/* Reset confirmation */}
      {confirmReset && (
        <div className="pt-popup-backdrop" onClick={() => setConfirmReset(false)}>
          <div className="pt-popup pt-confirm" onClick={e => e.stopPropagation()}>
            <p className="pt-popup-title">Reset the board?</p>
            <p className="pt-confirm-text muted">This reshuffles the deck and clears the field, hand, and graveyard. This can't be undone.</p>
            <div className="pt-pos-actions">
              <button type="button" className="btn-danger" onClick={() => { reset(); setConfirmReset(false); }}>Reset</button>
              <button type="button" onClick={() => setConfirmReset(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Leave-playtest confirmation */}
      {confirmLeave && (
        <div className="pt-popup-backdrop" onClick={() => setConfirmLeave(false)}>
          <div className="pt-popup pt-confirm" onClick={e => e.stopPropagation()}>
            <p className="pt-popup-title">Leave the playtest?</p>
            <p className="pt-confirm-text muted">Your current board will be discarded.</p>
            <div className="pt-pos-actions">
              <button type="button" className="btn-danger" onClick={() => navigate(`/deck/${id}`)}>Leave</button>
              <button type="button" onClick={() => setConfirmLeave(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Position menu (press-and-hold a placed card) */}
      {menuFor && menuCard && (
        <div className="pt-popup-backdrop" onClick={() => setMenuFor(null)}>
          <div className="pt-popup pt-pos-popup" style={anchorStyle(menuFor)} onClick={e => e.stopPropagation()}>
            <p className="pt-popup-title">{menuCard.name}</p>
            <div className="pt-pos-grid">
              {slicesFor(menuFor).map(o => (
                <PosOption key={o.label} name={menuCard.name} faceUp={o.faceUp} pos={o.pos} label={o.label}
                  onClick={() => { setPos(menuFor, o.faceUp, o.pos); setMenuFor(null); }} />
              ))}
            </div>
            <div className="pt-pos-actions">
              <button type="button" onClick={() => { pileMove(menuCard.uid, menuFor, 'gy'); setMenuFor(null); }}>Send to Graveyard</button>
              <button type="button" onClick={() => { pileMove(menuCard.uid, menuFor, 'banished'); setMenuFor(null); }}>Banish</button>
              <button type="button" onClick={() => { pileMove(menuCard.uid, menuFor, 'hand'); setMenuFor(null); }}>Return to Hand</button>
            </div>
            <button type="button" className="pt-popup-cancel" onClick={() => setMenuFor(null)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
